import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingCart, Plus, Minus, X, ArrowRight, Truck, FileText, Loader2, Check, ShieldCheck } from "lucide-react";
import { Header } from "@/components/sections/Header";
import { Footer } from "@/components/sections/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function QuoteModal({ open, onClose }) {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/supply/quote`, f);
      toast.success("Quote request sent — we'll reach out shortly.");
      setF({ name: "", company: "", email: "", phone: "", message: "" });
      onClose();
    } catch {
      toast.error("Could not send request. Please try again.");
    } finally { setLoading(false); }
  };

  const inp = "w-full border border-slate-300 rounded-sm px-4 py-3 focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none text-base";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" data-testid="quote-modal">
      <div className="bg-white rounded-sm w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#e02329]">Elite Supply Co.</p>
            <h3 className="text-xl font-bold text-[#1c4f9c]">Request a Bulk Quote</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="text-sm text-slate-500">For commercial accounts and large orders — tell us what you need and we'll send pricing.</p>
          <input data-testid="quote-name" value={f.name} onChange={up("name")} placeholder="Full name" className={inp} required />
          <input data-testid="quote-company" value={f.company} onChange={up("company")} placeholder="Company (optional)" className={inp} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="quote-email" type="email" value={f.email} onChange={up("email")} placeholder="Email" className={inp} required />
            <input data-testid="quote-phone" value={f.phone} onChange={up("phone")} placeholder="Phone" className={inp} required />
          </div>
          <textarea data-testid="quote-message" value={f.message} onChange={up("message")} rows={4} placeholder="What products and quantities are you looking for?" className={inp} required />
          <button type="submit" disabled={loading} data-testid="quote-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#e02329] text-white font-bold py-3.5 rounded-sm hover:bg-[#c81e24] transition-colors disabled:opacity-70">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <FileText size={18} />} Send Request
          </button>
        </form>
      </div>
    </div>
  );
}

export default function SupplyStore() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Elite Supply Co. | Commercial Cleaning Supplies";
    Promise.all([
      axios.get(`${API}/supply/products`),
      axios.get(`${API}/supply/categories`),
    ]).then(([p, c]) => {
      setProducts(p.data);
      setCategories(["All", ...c.data]);
    }).catch(() => toast.error("Could not load products.")).finally(() => setLoading(false));
    return () => { document.title = "Elite Facility Solutions | Commercial Cleaning & Janitorial Services"; };
  }, []);

  const visible = useMemo(
    () => (category === "All" ? products : products.filter((p) => p.category === category)),
    [products, category]
  );

  const addToCart = (p) => {
    setCart((c) => {
      const idx = c.findIndex((i) => i.id === p.id);
      if (idx >= 0) { const n = [...c]; n[idx].qty += 1; return n; }
      return [...c, { id: p.id, name: p.name, image: p.image, price: p.price, shipping_fee: p.shipping_fee, qty: 1 }];
    });
    setCartOpen(true);
    toast.success(`${p.name} added to cart`);
  };
  const setQty = (i, d) => setCart((c) => { const n = [...c]; n[i].qty += d; if (n[i].qty <= 0) n.splice(i, 1); return n; });

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const shipping = cart.reduce((s, i) => s + (i.shipping_fee || 0) * i.qty, 0);
  const total = subtotal + shipping;
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const checkout = async () => {
    if (!cart.length) { toast.error("Your cart is empty."); return; }
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/supply/checkout`, {
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed.");
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="supply-store">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#1c4f9c] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.pexels.com/photos/34516664/pexels-photo-34516664.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-white/80">Elite Supply Co.</p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] max-w-3xl">
            Commercial cleaning supplies, sourced &amp; shipped fast.
          </h1>
          <p className="mt-5 max-w-xl text-white/80 text-base sm:text-lg">
            Auto scrubbers, paper products, chemicals and janitorial gear from reliable suppliers — with fast shipping across Florida.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#supply-products" data-testid="shop-now-btn" className="inline-flex items-center gap-2 bg-[#e02329] text-white font-bold py-3.5 px-7 rounded-sm hover:bg-[#c81e24] transition-colors">
              Shop Supplies <ArrowRight size={18} />
            </a>
            <button onClick={() => setQuoteOpen(true)} data-testid="open-quote-btn" className="inline-flex items-center gap-2 border-2 border-white text-white font-bold py-3.5 px-7 rounded-sm hover:bg-white hover:text-[#1c4f9c] transition-colors">
              <FileText size={18} /> Request a Bulk Quote
            </button>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-sm text-white/70">
            <span className="inline-flex items-center gap-2"><Truck size={16} /> Fast shipping to Florida</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck size={16} /> Reliable US suppliers</span>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="supply-products" className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e02329]">Catalog</p>
            <h2 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-[#1c4f9c]">Shop by category</h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)} data-testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`text-sm font-bold px-4 py-2 rounded-sm border-2 transition-colors ${category === c ? "bg-[#1c4f9c] border-[#1c4f9c] text-white" : "border-slate-200 text-slate-600 hover:border-[#1c4f9c]"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1c4f9c]" size={32} /></div>
        ) : visible.length === 0 ? (
          <p className="text-center text-slate-400 py-20">No products in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((p) => (
              <div key={p.id} data-testid={`supply-product-${p.id}`} className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                <div className="aspect-square bg-slate-100 overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#e02329]">{p.category}</span>
                  <h3 className="mt-1 text-lg font-bold text-[#1c4f9c] leading-snug">{p.name}</h3>
                  <p className="mt-1.5 text-sm text-slate-500 leading-relaxed flex-1">{p.description}</p>
                  <p className="mt-2 text-xs text-slate-400 inline-flex items-center gap-1"><Truck size={13} /> {p.shipping_fee > 0 ? `+ ${money(p.shipping_fee)} shipping` : "Free shipping"}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-2xl font-bold text-[#1c4f9c]">{money(p.price)}</span>
                    <button onClick={() => addToCart(p)} data-testid={`add-supply-${p.id}`} className="inline-flex items-center gap-2 bg-[#e02329] text-white font-bold text-sm py-2.5 px-4 rounded-sm hover:bg-[#c81e24] transition-colors">
                      <Plus size={16} /> Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Floating cart button */}
      <button onClick={() => setCartOpen(true)} data-testid="open-supply-cart" className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 bg-[#1c4f9c] text-white font-bold py-3.5 px-6 rounded-sm shadow-xl hover:bg-[#163f7d] transition-colors">
        <ShoppingCart size={20} /> Cart
        {cartCount > 0 && <span className="bg-[#e02329] text-white text-xs w-6 h-6 flex items-center justify-center rounded-full" data-testid="supply-cart-count">{cartCount}</span>}
      </button>

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="supply-cart-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="relative w-full max-w-md bg-white h-full flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-xl font-bold text-[#1c4f9c]">Your Cart</h3>
              <button onClick={() => setCartOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {cart.length === 0 ? <p className="text-slate-400 text-center py-12">Your cart is empty.</p> : cart.map((it, i) => (
                <div key={it.id} className="flex gap-3 items-center border border-slate-200 rounded-sm p-3">
                  <img src={it.image} alt="" className="w-16 h-16 object-cover rounded-sm bg-slate-100" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1c4f9c] leading-tight">{it.name}</p>
                    <p className="text-xs text-slate-400">{money(it.price)} · +{money(it.shipping_fee)} ship</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(i, -1)} data-testid={`supply-minus-${it.id}`} className="w-7 h-7 flex items-center justify-center border border-slate-200 rounded-sm text-slate-600"><Minus size={14} /></button>
                    <span className="w-5 text-center text-sm font-bold">{it.qty}</span>
                    <button onClick={() => setQty(i, 1)} data-testid={`supply-plus-${it.id}`} className="w-7 h-7 flex items-center justify-center border border-slate-200 rounded-sm text-slate-600"><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-slate-200 space-y-2">
              <div className="flex justify-between text-sm text-slate-600"><span>Subtotal</span><span data-testid="supply-subtotal">{money(subtotal)}</span></div>
              <div className="flex justify-between text-sm text-slate-600"><span>Shipping</span><span data-testid="supply-shipping">{money(shipping)}</span></div>
              <div className="flex justify-between font-bold text-[#1c4f9c] text-lg pt-1"><span>Total</span><span data-testid="supply-total">{money(total)}</span></div>
              <button onClick={checkout} disabled={placing || !cart.length} data-testid="supply-checkout-btn" className="w-full inline-flex items-center justify-center gap-2 bg-[#e02329] text-white font-bold py-4 rounded-sm hover:bg-[#c81e24] transition-colors disabled:opacity-50 mt-2">
                {placing ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} {placing ? "Redirecting…" : "Secure Checkout"}
              </button>
              <p className="text-center text-xs text-slate-400">You'll enter shipping & payment securely via Stripe. Guest checkout — no account needed.</p>
            </div>
          </div>
        </div>
      )}

      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
      <Footer />
    </div>
  );
}
